import React, { useEffect, useMemo, useState } from 'react'
import {
  getAllContacts,
  updateContactStatus,
  deleteContact,
} from '../services/contactService'
import {
  Avatar,
  Banner,
  EmptyState,
  IconButton,
  Pill,
  SearchInput,
  StatTiles,
  Tabs,
  Toast,
  capitalize,
  formatDate,
  timeAgo,
  useConfirm,
  useFlash,
} from './AdminUI'

const CONTACT_STATUSES = ['new', 'read', 'replied', 'archived']

const STATUS_META = {
  new: { tone: 'blue', icon: 'fa-circle' },
  read: { tone: 'gray', icon: 'fa-envelope-open' },
  replied: { tone: 'green', icon: 'fa-reply' },
  archived: { tone: 'violet', icon: 'fa-box-archive' },
}

const contactId = (c) => c.id || c._id

function ContactsManagement({ onContactsChange }) {
  const [contacts, setContacts] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useFlash()
  const [confirm, confirmDialog] = useConfirm()
  const [statusFilter, setStatusFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState(null)
  const [saving, setSaving] = useState(false)

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null

  const loadContacts = async () => {
    setLoading(true)
    setError('')
    try {
      const data = await getAllContacts({}, token)
      const list = Array.isArray(data) ? data : []
      setContacts(list)
      onContactsChange?.(list)
    } catch (err) {
      setError(err.message || 'Failed to load messages')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadContacts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const counts = useMemo(() => {
    const result = { all: contacts.length }
    CONTACT_STATUSES.forEach((s) => {
      result[s] = contacts.filter((c) => (c.status || 'new') === s).length
    })
    return result
  }, [contacts])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return contacts
      .filter((c) => {
        if (statusFilter !== 'all' && (c.status || 'new') !== statusFilter) return false
        if (!q) return true
        return [c.fullName, c.email, c.phone, c.subject, c.serviceInterest, c.message]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(q)
      })
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
  }, [contacts, statusFilter, search])

  const selected = useMemo(
    () => contacts.find((c) => contactId(c) === selectedId) || null,
    [contacts, selectedId]
  )

  const mergeContact = (id, updated) => {
    setContacts((prev) => {
      const next = prev.map((c) => (contactId(c) === id ? { ...c, ...updated } : c))
      onContactsChange?.(next)
      return next
    })
  }

  const openContact = async (contact) => {
    const id = contactId(contact)
    setSelectedId(id)
    if ((contact.status || 'new') === 'new') {
      try {
        const result = await updateContactStatus(id, 'read', token)
        mergeContact(id, result.contact || { status: 'read' })
      } catch {
        // marking as read is best-effort
      }
    }
  }

  const handleStatus = async (id, status) => {
    setSaving(true)
    setError('')
    try {
      const result = await updateContactStatus(id, status, token)
      mergeContact(id, result.contact || { status })
      setSuccess(`Message marked as ${status}`)
    } catch (err) {
      setError(err.message || 'Failed to update status')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = (contact) => {
    const id = contactId(contact)
    return confirm({
      title: 'Delete this message?',
      message: 'The enquiry will be permanently removed from your inbox.',
      item: {
        media: <Avatar name={contact.fullName || contact.email} size="lg" tone="soft" />,
        title: contact.fullName || contact.email,
        meta: contact.subject || contact.serviceInterest || contact.email || 'Website enquiry',
      },
      points: [
        'This action cannot be undone.',
        ...(contact.status === 'new' ? ["You haven't replied to this message yet."] : []),
      ],
      confirmLabel: 'Delete message',
      busyLabel: 'Deleting…',
      onConfirm: async () => {
        setSaving(true)
        setError('')
        try {
          await deleteContact(id, token)
          setContacts((prev) => {
            const next = prev.filter((c) => contactId(c) !== id)
            onContactsChange?.(next)
            return next
          })
          setSelectedId(null)
          setSuccess('Message deleted')
        } catch (err) {
          setError(err.message || 'Failed to delete message')
        } finally {
          setSaving(false)
        }
      },
    })
  }

  const stats = [
    { id: 'all', label: 'All messages', value: counts.all, hint: 'Website enquiries', icon: 'fa-inbox', tone: 'blue', filter: 'all' },
    { id: 'new', label: 'Unread', value: counts.new, hint: 'Awaiting first look', icon: 'fa-envelope', tone: 'amber', filter: 'new' },
    { id: 'replied', label: 'Replied', value: counts.replied, hint: 'Conversation handled', icon: 'fa-reply', tone: 'green', filter: 'replied' },
    { id: 'archived', label: 'Archived', value: counts.archived, hint: 'Closed enquiries', icon: 'fa-box-archive', tone: 'violet', filter: 'archived' },
  ]

  const replyHref = selected
    ? `mailto:${selected.email}?subject=${encodeURIComponent(`Re: ${selected.subject || 'Your enquiry'}`)}`
    : '#'

  return (
    <div className="ax">
      <StatTiles items={stats} active={statusFilter} onSelect={setStatusFilter} />

      {error ? <Banner onClose={() => setError('')}>{error}</Banner> : null}

      <div className={`ax-card ax-inbox ${selected ? 'has-selection' : ''}`}>
        <div className="ax-inbox__list">
          <div className="ax-inbox__tools">
            <div className="ax-inbox__search">
              <SearchInput value={search} onChange={setSearch} placeholder="Search messages…" label="Search messages" />
              <IconButton
                icon={loading ? 'fa-spinner fa-spin' : 'fa-rotate'}
                label="Refresh messages"
                className="ax-icon-btn--outline"
                onClick={loadContacts}
                disabled={loading}
              />
            </div>
            <Tabs
              value={statusFilter}
              onChange={setStatusFilter}
              label="Message status"
              options={[
                { id: 'all', label: 'All', count: counts.all },
                { id: 'new', label: 'Unread', count: counts.new },
                { id: 'replied', label: 'Replied' },
                { id: 'archived', label: 'Archived' },
              ]}
            />
          </div>

          <div className="ax-inbox__items">
            {loading && !contacts.length ? (
              Array.from({ length: 5 }, (_, i) => <div key={i} className="ax-msg ax-msg--skeleton" />)
            ) : filtered.length === 0 ? (
              <EmptyState
                icon="fa-envelope-open-text"
                title={contacts.length ? 'No messages here' : 'Inbox is empty'}
                text={contacts.length ? 'Try another filter or search.' : 'Contact form submissions will show up here.'}
              />
            ) : (
              filtered.map((contact) => {
                const id = contactId(contact)
                const status = contact.status || 'new'
                return (
                  <button
                    key={id}
                    type="button"
                    className={`ax-msg ${selectedId === id ? 'is-active' : ''} ${status === 'new' ? 'is-unread' : ''}`}
                    onClick={() => openContact(contact)}
                  >
                    <Avatar name={contact.fullName} tone={status === 'new' ? undefined : 'soft'} />
                    <span className="ax-msg__body">
                      <span className="ax-msg__top">
                        <strong>{contact.fullName || contact.email}</strong>
                        <time>{timeAgo(contact.createdAt)}</time>
                      </span>
                      <span className="ax-msg__subject">{contact.subject || 'General enquiry'}</span>
                      <span className="ax-msg__snippet">{contact.message}</span>
                      <span className="ax-msg__meta">
                        {contact.serviceInterest ? <span className="ax-tag">{contact.serviceInterest}</span> : null}
                        {status !== 'new' ? <Pill tone={STATUS_META[status]?.tone || 'gray'}>{status}</Pill> : null}
                      </span>
                    </span>
                    {status === 'new' ? <span className="ax-msg__dot" aria-label="Unread" /> : null}
                  </button>
                )
              })
            )}
          </div>
        </div>

        <div className="ax-inbox__reader">
          {selected ? (
            <>
              <header className="ax-reader__head">
                <IconButton icon="fa-arrow-left" label="Back to inbox" className="ax-reader__back" onClick={() => setSelectedId(null)} />
                <div className="ax-reader__title">
                  <h2>{selected.subject || 'General enquiry'}</h2>
                  <p>Received {formatDate(selected.createdAt, true)}</p>
                </div>
                <div className="ax-row-actions">
                  <IconButton
                    icon="fa-box-archive"
                    label="Archive"
                    disabled={saving || selected.status === 'archived'}
                    onClick={() => handleStatus(contactId(selected), 'archived')}
                  />
                  <IconButton
                    icon="fa-envelope"
                    label="Mark as unread"
                    disabled={saving || selected.status === 'new'}
                    onClick={() => handleStatus(contactId(selected), 'new')}
                  />
                  <IconButton
                    icon="fa-trash-can"
                    label="Delete message"
                    tone="danger"
                    disabled={saving}
                    onClick={() => handleDelete(selected)}
                  />
                </div>
              </header>

              <div className="ax-reader__scroll">
                <div className="ax-sender">
                  <Avatar name={selected.fullName} size="lg" />
                  <div className="ax-cell__text">
                    <strong>{selected.fullName}</strong>
                    <a href={`mailto:${selected.email}`} className="ax-link">
                      {selected.email}
                    </a>
                  </div>
                  <Pill tone={STATUS_META[selected.status || 'new']?.tone || 'gray'}>{selected.status || 'new'}</Pill>
                </div>

                <div className="ax-kv-grid ax-kv-grid--3">
                  <div className="ax-kv">
                    <small>Phone</small>
                    {selected.phone ? (
                      <a href={`tel:${selected.phone}`} className="ax-link ax-strong">
                        {selected.phone}
                      </a>
                    ) : (
                      <strong>—</strong>
                    )}
                  </div>
                  <div className="ax-kv">
                    <small>Service interest</small>
                    <strong>{selected.serviceInterest || '—'}</strong>
                  </div>
                  <div className="ax-kv">
                    <small>Received</small>
                    <strong>{timeAgo(selected.createdAt) || '—'}</strong>
                  </div>
                </div>

                <article className="ax-letter">{selected.message}</article>
              </div>

              <footer className="ax-reader__foot">
                <div className="ax-reader__status">
                  <span className="ax-field__label">Status</span>
                  <div className="ax-tabs">
                    {CONTACT_STATUSES.map((s) => (
                      <button
                        key={s}
                        type="button"
                        className={`ax-tabs__tab ${(selected.status || 'new') === s ? 'is-active' : ''}`}
                        disabled={saving}
                        onClick={() => handleStatus(contactId(selected), s)}
                      >
                        {capitalize(s)}
                      </button>
                    ))}
                  </div>
                </div>
                <span className="ax-foot__spacer" />
                {selected.phone ? (
                  <a className="ax-btn" href={`tel:${selected.phone}`}>
                    <i className="fas fa-phone" aria-hidden="true" />
                    <span>Call</span>
                  </a>
                ) : null}
                <a
                  className="ax-btn ax-btn--primary"
                  href={replyHref}
                >
                  <i className="fas fa-reply" aria-hidden="true" />
                  <span>Reply by email</span>
                </a>
              </footer>
            </>
          ) : (
            <EmptyState
              icon="fa-envelope-open"
              title="Select a message"
              text="Choose an enquiry from the list to read it and reply."
            />
          )}
        </div>
      </div>

      <Toast message={success} />
      {confirmDialog}
    </div>
  )
}

export default ContactsManagement
